import { useState, useRef, useEffect, memo } from "react";
import { BACKEND_URL } from "@/lib/api";
import { SITE_DOMAIN } from "@/components/broadcast/shared";

// Cinematic AI promo: sequences the promo's 10s Seedance clips with a seamless
// crossfade while its OpenAI TTS voiceover plays over the top. The voiceover
// ducks the music bed via the "gawx-promo-speaking" window event. ffmpeg isn't
// available server-side, so the clips are stitched here in the browser.
function PromoSlideBase({ promo }) {
  const clips = promo?.clips || [];
  const [i, setI] = useState(0);
  const vidRefs = useRef([]);
  const audioRef = useRef(null);
  const isEncoder =
    typeof document !== "undefined" && document.documentElement.classList.contains("encoder-lite");

  useEffect(() => { setI(0); }, [promo?.key]);

  // Play the voiceover and duck the bed for the length of the promo airing.
  useEffect(() => {
    const a = audioRef.current;
    if (a) { try { a.currentTime = 0; } catch { /* ignore */ } a.play?.().catch(() => {}); }
    window.dispatchEvent(new CustomEvent("gawx-promo-speaking", { detail: { speaking: true } }));
    return () => window.dispatchEvent(new CustomEvent("gawx-promo-speaking", { detail: { speaking: false } }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [promo?.key]);

  // Only the active clip plays; the rest stay paused (keeps the encoder calm).
  useEffect(() => {
    vidRefs.current.forEach((v, n) => {
      if (!v) return;
      if (n === i) { try { v.currentTime = 0; } catch { /* ignore */ } v.play?.().catch(() => {}); }
      else { v.pause?.(); }
    });
  }, [i]);

  if (!clips.length) return null;
  const messages = promo.messages || [];
  const msg = i >= clips.length - 1 ? (messages[messages.length - 1] || "") : (messages[0] || "");

  return (
    <div data-testid={`promo-slide-${promo.key}`} className="relative h-full w-full overflow-hidden bg-black">
      {clips.map((c, n) => (
        <video
          key={c.url}
          ref={(el) => (vidRefs.current[n] = el)}
          src={`${BACKEND_URL}${c.url}`}
          muted
          playsInline
          preload="auto"
          onEnded={() => setI((p) => (n === p && p + 1 < clips.length ? p + 1 : p))}
          onError={() => setI((p) => (n === p && p + 1 < clips.length ? p + 1 : p))}
          className="absolute inset-0 h-full w-full object-cover transition-opacity duration-700"
          style={{ opacity: n === i ? 1 : 0 }}
        />
      ))}

      {/* Cinematic top/bottom darkening for text legibility */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{ background: "linear-gradient(180deg, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0) 32%, rgba(0,0,0,0) 52%, rgba(0,0,0,0.8) 100%)" }}
      />

      {/* Network lockup */}
      <div className="pointer-events-none absolute left-10 top-8 flex items-center gap-3">
        <div className="flex h-12 w-12 items-center justify-center rounded-[6px] bg-[#d90429] font-oswald text-[26px] font-black text-white shadow-[0_6px_20px_rgba(0,0,0,0.6)]">G</div>
        <div className="font-oswald text-[22px] font-black uppercase tracking-[0.18em] text-white drop-shadow-[0_2px_8px_rgba(0,0,0,0.85)]">Georgia Weather Network</div>
      </div>

      {/* Headline */}
      <div className="pointer-events-none absolute inset-x-0 bottom-0 flex flex-col items-start gap-5 px-16 pb-20">
        <div
          key={msg}
          className="promo-rise font-oswald text-[96px] font-black uppercase leading-[0.95] tracking-wide text-white drop-shadow-[0_6px_24px_rgba(0,0,0,0.95)]"
          data-testid="promo-headline"
        >
          {msg}
        </div>
        <div className="h-[6px] w-44 bg-[#ffd60a]" />
        <div className="font-oswald text-[30px] font-bold uppercase tracking-[0.3em] text-[#ffd60a] drop-shadow-[0_2px_10px_rgba(0,0,0,0.95)]">
          24/7 · {SITE_DOMAIN}
        </div>
      </div>

      <audio ref={audioRef} data-testid={`promo-voice-${promo.key}`} src={`${BACKEND_URL}${promo.voice_url}`} preload="auto" muted={!isEncoder} />
    </div>
  );
}

export const PromoSlide = memo(PromoSlideBase);
